import { Component, OnInit, inject } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { Dia, IControlHorarioDetalles, ITipoControlHorario } from 'modelos/src';

@Component({
  selector: 'app-agregar-horario-completo',
  templateUrl: './agregar-horario-completo.component.html',
  styleUrl: './agregar-horario-completo.component.scss',
  standalone: false,
})
export class AgregarHorarioCompletoComponent implements OnInit {
  data = inject<{
    horarioEditar?: IControlHorarioDetalles;
    tipoInicial?: ITipoControlHorario;
}>(MAT_DIALOG_DATA);
  helper = inject(HelperService);
  dialogRef = inject<MatDialogRef<AgregarHorarioCompletoComponent>>(MatDialogRef);

  public form?: FormGroup;
  public title?: string;

  // Opciones para los selectores
  public tiposControlHorario: ITipoControlHorario[] = ['Apertura', 'Cierre'];
  public diasSemana: Dia[] = [
    'Lunes',
    'Martes',
    'Miercoles',
    'Jueves',
    'Viernes',
    'Sabado',
    'Domingo',
  ];

  // Validador personalizado para múltiplos de 15
  private multiplo15Validator(
    control: AbstractControl,
  ): ValidationErrors | null {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (value % 15 !== 0) {
      return { multiplo15: true };
    }
    return null;
  }

  // Validador para asegurar que al menos una tolerancia sea mayor a 0
  private alMenosUnaToleranciaValidator(
    group: AbstractControl,
  ): ValidationErrors | null {
    if (!(group instanceof FormGroup)) {
      return null;
    }
    const toleranciaAntes = group.get('toleranciaAntes')?.value ?? 0;
    const toleranciaDespues = group.get('toleranciaDespues')?.value ?? 0;

    if (toleranciaAntes === 0 && toleranciaDespues === 0) {
      return { alMenosUnaTolerancia: true };
    }
    return null;
  }

  private parseHora(hora: string): [number, number] | null {
    const horaTrim = hora?.trim();
    const match = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(horaTrim);
    if (!match) {
      return null;
    }

    let h = Number(match[1]);
    let m = Number(match[2]);

    // Normaliza siempre en pasos de 15 minutos para mantener el comportamiento del selector.
    m = Math.round(m / 15) * 15;
    if (m === 60) {
      m = 0;
      h = (h + 1) % 24;
    }

    return [h, m];
  }

  private horaInicialFormateada(): string {
    const horaNormalizada = this.parseHora(
      this.data?.horarioEditar?.hora ?? '',
    );
    if (!horaNormalizada) {
      return '08:00';
    }

    const [h, m] = horaNormalizada;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }

  private createForm() {
    this.form = new FormGroup(
      {
        tipo: new FormControl(
          this.data?.horarioEditar?.tipo || this.data?.tipoInicial,
          Validators.required,
        ),
        dias: new FormControl(
          this.data?.horarioEditar?.dias || [],
          Validators.required,
        ),
        hora: new FormControl(
          this.horaInicialFormateada(),
          Validators.required,
        ),
        toleranciaAntes: new FormControl(
          (this.data?.horarioEditar?.toleranciaAntes ??
            this.data?.tipoInicial === 'Apertura')
            ? 30
            : 0,
          [Validators.required, Validators.min(0), this.multiplo15Validator],
        ),
        toleranciaDespues: new FormControl(
          (this.data?.horarioEditar?.toleranciaDespues ??
            this.data?.tipoInicial === 'Cierre')
            ? 30
            : 0,
          [Validators.required, Validators.min(0), this.multiplo15Validator],
        ),
        particion: new FormControl(this.data?.horarioEditar?.particion, [
          Validators.pattern('^[0-9]*$'),
        ]),
      },
      { validators: this.alMenosUnaToleranciaValidator },
    );

    // Suscribirse a cambios en el tipo de control
    this.form
      .get('tipo')
      ?.valueChanges.subscribe((tipo: ITipoControlHorario) => {
        if (!this.data?.horarioEditar) {
          // Solo aplicar valores por defecto si NO estamos editando
          if (tipo === 'Apertura') {
            this.form?.get('toleranciaAntes')?.setValue(30);
            this.form?.get('toleranciaDespues')?.setValue(0);
          } else if (tipo === 'Cierre') {
            this.form?.get('toleranciaAntes')?.setValue(0);
            this.form?.get('toleranciaDespues')?.setValue(30);
          }
        }
      });
  }

  public async onSubmit() {
    if (this.form.valid) {
      this.dialogRef.close(this.form);
    }
  }

  public incrementarToleranciaAntes(): void {
    const currentValue = this.form?.get('toleranciaAntes')?.value ?? 0;
    this.form?.get('toleranciaAntes')?.setValue(currentValue + 15);
  }

  public decrementarToleranciaAntes(): void {
    const currentValue = this.form?.get('toleranciaAntes')?.value ?? 0;
    const toleranciaDespues = this.form?.get('toleranciaDespues')?.value ?? 0;

    // No permitir decrementar si ambas tolerancias quedarían en 0
    if (currentValue >= 15) {
      const nuevoValor = currentValue - 15;
      if (nuevoValor === 0 && toleranciaDespues === 0) {
        // No hacer nada, al menos una debe ser > 0
        return;
      }
      this.form?.get('toleranciaAntes')?.setValue(nuevoValor);
    }
  }

  public incrementarToleranciaDespues(): void {
    const currentValue = this.form?.get('toleranciaDespues')?.value ?? 0;
    this.form?.get('toleranciaDespues')?.setValue(currentValue + 15);
  }

  public decrementarToleranciaDespues(): void {
    const currentValue = this.form?.get('toleranciaDespues')?.value ?? 0;
    const toleranciaAntes = this.form?.get('toleranciaAntes')?.value ?? 0;

    // No permitir decrementar si ambas tolerancias quedarían en 0
    if (currentValue >= 15) {
      const nuevoValor = currentValue - 15;
      if (nuevoValor === 0 && toleranciaAntes === 0) {
        // No hacer nada, al menos una debe ser > 0
        return;
      }
      this.form?.get('toleranciaDespues')?.setValue(nuevoValor);
    }
  }

  public seleccionarLunesViernes(): void {
    const diasLaborales: Dia[] = [
      'Lunes',
      'Martes',
      'Miercoles',
      'Jueves',
      'Viernes',
    ];
    this.form?.get('dias')?.setValue(diasLaborales);
  }

  public seleccionarTodosDias(): void {
    this.form?.get('dias')?.setValue([...this.diasSemana]);
  }

  public seleccionarSabadoDomingo(): void {
    const finDeSemana: Dia[] = ['Sabado', 'Domingo'];
    this.form?.get('dias')?.setValue(finDeSemana);
  }

  public limpiarDias(): void {
    this.form?.get('dias')?.setValue([]);
  }

  public getNombreDiaConTilde(dia: Dia): string {
    // Mostrar con tilde solo para visualización, sin afectar las comparaciones
    if (dia === 'Miercoles') return 'Miércoles';
    if (dia === 'Sabado') return 'Sábado';
    return dia;
  }

  async ngOnInit() {
    this.title = this.data?.horarioEditar
      ? 'Editar Horario'
      : 'Agregar Horario';
    this.createForm();
  }
}
